<script lang="ts">
  import { navigate } from '../lib/router.svelte';
  import AdminShell from './AdminShell.svelte';
  import PageContainer from '../lib/ui/PageContainer.svelte';
  import TrendChart from '../lib/charts/TrendChart.svelte';
  import Heatmap from '@ui/Heatmap.svelte';
  import Clock from '../lib/charts/Clock.svelte';
  import Donut from '../lib/charts/Donut.svelte';
  import Avatar from '@ui/Avatar.svelte';
  import StatCard from '../lib/ui/StatCard.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import StageBadge from '../lib/ui/StageBadge.svelte';
  import MemberHover from '@ui/MemberHover.svelte';
  import RoleJourney from '@ui/RoleJourney.svelte';
  import Drawer from '../lib/ui/Drawer.svelte';
  import ContentSummary from '../lib/ui/ContentSummary.svelte';
  import ContributorsEngagement from '../lib/ui/ContributorsEngagement.svelte';
  import RevenueTiles from '../lib/ui/RevenueTiles.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';
  import {
    redirectIfAwaitingOnboarding,
    getToken,
    handle401,
    parseTs,
    utcDayStartISO,
    utcDayEndISO,
    type AdminUser,
    type MemberStage,
  } from '../lib/admin';
  import { community, withCommunity, onCommunityChange } from '../lib/community.svelte';
  import { asAdminUser, ensureBootstrap } from '../lib/session';
  import { fetchMemberCard, journeySegments, type DenseActivity, type Member } from '@api';
  import { CONTRIBUTORS_CARD_BLOCKS, OVERVIEW_BLOCKS, YEAR, fetchInsights, lastFor, type ContributorRow, type InsightsWith, type MomentumRow } from '@api';

  // THREE FETCHES BECOME ONE (doc 04 §4.3). The page used to issue
  // `/admin/analytics/community` + `/admin/analytics/content` + a whole
  // `/admin/analytics/contributors` payload — up to 1,500 scatter points and the
  // full cohort grid — to fill two donuts from nine of its numbers (§5.2). It also
  // paid for the SAME clock grid twice, because the first two endpoints each
  // computed and shipped their own copy. One request, thirteen named blocks, and
  // the scatter is never built.
  type Data = InsightsWith<(typeof OVERVIEW_BLOCKS)[number]>;

  /** The Contributors & Engagement card's own payload — see `CONTRIBUTORS_CARD_BLOCKS`.
   *  It is a SECOND request to the same route because the card owns a period and this
   *  page's `last` is pinned to the year the heatmap draws. */
  type CardData = InsightsWith<(typeof CONTRIBUTORS_CARD_BLOCKS)[number]>;

  // The page window, stated once: it is what `?last=` sends and what the heatmap's
  // header describes, so the two cannot drift.
  const WINDOW_DAYS = 365;

  let me = $state<AdminUser | null>(null);
  let data = $state<Data | null>(null);
  let loading = $state(true);
  let dataLoading = $state(false);
  let error = $state<string | null>(null);

  // --- Contributors & Engagement card -----------------------------------------
  // Its own state, its own request, its own period. Kept beside the page's rather
  // than folded into it: a period change must reload THIS card and not re-issue the
  // 365-day page payload (13 blocks, the heatmap and the trend among them).
  let card = $state<CardData | null>(null);
  let cardDays = $state(90);
  let cardLoading = $state(false);
  let cardError = $state<string | null>(null);

  /** Identity of what the card has on screen: it reloads for a community switch AND
   *  for a period change, so both belong in the dedupe key. */
  const cardKey = (id: number, d: number): string => `${id}:${d}`;

  let memberId = $state<number | null>(null);
  let member = $state<Member<DenseActivity> | null>(null);
  let memberLoading = $state(false);

  async function init(): Promise<void> {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    loading = true;
    error = null;
    try {
      // The page's own fetch goes out in the SAME round trip as `/v1/me`, against
      // the id already in localStorage (doc 04 §4.1 design point 2), instead of
      // waiting two round trips on `/admin/me` → `/admin/communities` to validate
      // an id the browser was holding all along.
      const [boot, insights, cardData] = await Promise.all([
        ensureBootstrap(),
        withCommunity((skoolId) => {
          watch.seen(skoolId);
          return fetchInsights(skoolId, OVERVIEW_BLOCKS, { last: YEAR });
        }),
        // The card's request rides the SAME round trip as the page's, against the
        // same optimistic id — two requests, still 1 RTT. It is FAIL-SOFT on
        // purpose: the card is one section, so a failure there greys that section
        // rather than replacing the whole Overview with an error. A 401 still
        // reaches `handle401` through the page's own request, which shares the token.
        withCommunity((skoolId) => {
          cardWatch.seen(cardKey(skoolId, cardDays));
          return fetchInsights(skoolId, CONTRIBUTORS_CARD_BLOCKS, { last: lastFor(cardDays) });
        }).catch((err: unknown) => {
          cardError = err instanceof Error ? err.message : 'Failed to load contributors.';
          return null;
        }),
      ]);
      me = asAdminUser(boot);
      if (redirectIfAwaitingOnboarding(me, navigate)) return;
      data = insights;
      card = cardData;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load.';
    } finally {
      loading = false;
    }
  }

  // Reload whenever the shared community selection changes. `loadedFor` is what the
  // request already on the wire asked for, so the bootstrap resolving the selection
  // to the id we optimistically used does not fire a second identical request.
  const watch = onCommunityChange((id) => void load(id), { gate: () => !loading });

  async function load(id: number): Promise<void> {
    dataLoading = true;
    error = null;
    member = null;
    memberId = null;
    watch.seen(id);
    try {
      data = await fetchInsights(id, OVERVIEW_BLOCKS, { last: YEAR });
    } catch (err: unknown) {
      // `loadedFor` deliberately KEEPS the id that failed. The effect above reads
      // it, so clearing it here would re-enter `load(id)` the instant this assigns
      // `error` — a tight retry loop against an endpoint that is already failing.
      // The retry is the error block's button, which re-runs `init`.
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load analytics.';
      data = null;
    } finally {
      dataLoading = false;
    }
  }

  // The card's watcher, separate from the page's because it keys on the period too.
  // A dial turn changes `cardKey` and re-runs THIS one only; the 365-day page
  // request is untouched, which is the whole reason the card has its own fetch.
  // `cardDays` is read unconditionally inside `key`, as `onCommunityChange` requires.
  const cardWatch = onCommunityChange((id) => void loadCard(id, cardDays), {
    gate: () => !loading,
    key: (id) => (id == null ? null : cardKey(id, cardDays)),
  });

  async function loadCard(id: number, d: number): Promise<void> {
    cardLoading = true;
    cardError = null;
    try {
      card = await fetchInsights(id, CONTRIBUTORS_CARD_BLOCKS, { last: lastFor(d) });
    } catch (err: unknown) {
      // Same reasoning as `load` above: the key that failed is deliberately KEPT, so
      // assigning `cardError` cannot re-enter this against an endpoint already
      // failing. `rearm()` on the card's own Retry is what allows another attempt.
      if (handle401(err, navigate)) return;
      cardError = err instanceof Error ? err.message : 'Failed to load contributors.';
      card = null;
    } finally {
      cardLoading = false;
    }
  }

  /** The card's Retry. `seen()` rather than `rearm()`: this IS the work the watcher
   *  would have done, so the key is recorded as handled — `rearm()` would leave it
   *  cleared and let the next reactive run re-issue the identical request. */
  function retryCard(): void {
    const id = community.selectedId;
    if (id == null) return;
    cardWatch.seen(cardKey(id, cardDays));
    void loadCard(id, cardDays);
  }

  /**
   * The drill-down drawer — doc 04 §4.1 W4, and the one waterfall on this page that
   * was a WRONG CHOICE rather than a missing route.
   *
   * It used to call `GET /admin/analytics/member`, which reads EVERY post and comment
   * the member ever wrote (`api/analytics.py:624`, "ALL of their posts/comments")
   * to build all-time trend and clock series, and then the drawer rendered a subset of
   * what came back. The cheap sibling — the same payload the hovercard has always used
   * — was mounted the whole time. In v1 they are one route and the difference is the
   * block selection, so this asks for identity + `stats` + `journey` + a dense
   * activity series and nothing else: precomputed columns and one heatmap build,
   * against a full content scan.
   *
   * WHAT THE CHEAP SET CANNOT SERVE, and is therefore no longer in the drawer:
   * `reply_network` / `reply_totals` (the conversation-partner list) and `bio`. Both
   * live in v1's `?include=analysis` / `?include=depth`, which answer 400 today
   * because neither aggregation exists in `domain/` (`api/v1/members.py` module
   * docstring). They are one click away on "Full profile →", which is still on the
   * legacy route for exactly that reason.
   */
  async function openMember(id: number): Promise<void> {
    if (community.selectedId == null) return;
    memberId = id;
    memberLoading = true;
    try {
      member = await fetchMemberCard(community.selectedId, id);
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      member = null;
    } finally {
      memberLoading = false;
    }
  }
  function closeMember(): void {
    memberId = null;
    member = null;
  }

  // --- formatting ---
  function pct(x: number | null | undefined): string {
    return x == null ? '—' : `${(x * 100).toFixed(x > 0 && x < 0.1 ? 1 : 0)}%`;
  }
  function num(x: number | null | undefined): string {
    return x == null ? '—' : x.toLocaleString();
  }
  function hrs(h: number | null | undefined): string {
    if (h == null) return '—';
    if (h < 1) return `${Math.round(h * 60)}m`;
    if (h < 48) return `${h.toFixed(1)}h`;
    return `${(h / 24).toFixed(1)}d`;
  }
  // `stats.first_active` / `stats.last_active` are UTC-AWARE INSTANTS in v1 (§1.5)
  // where `/admin/analytics/member` emitted a bare `YYYY-MM-DD` for the same keys, so
  // this goes through `parseTs` — `new Date()` reads a tz-less time-bearing string as
  // local and would shift the date by the viewer's offset.
  function fmtDate(d: string | null | undefined): string {
    return d ? parseTs(d).toLocaleDateString() : '—';
  }

  // Intensity ramp for the "less → more" legend (matches the Heatmap component).
  const HEAT = ['bg-base-content/12', 'bg-primary/45', 'bg-primary/65', 'bg-primary/85', 'bg-primary'];

  // The author card is NESTED on every v1 row and is null where the member could not
  // be resolved (§2.1: null, not a fake member). Both lists are flattened once, here,
  // and an unnameable row is dropped rather than rendered as "Unknown".
  interface PersonRow {
    member_id: number;
    name: string;
    picture_profile: string | null;
    recent_engagement: number;
    prior_engagement: number;
    stage: MemberStage;
    momentum: number;
  }
  function people(rows: MomentumRow[]): PersonRow[] {
    return rows.flatMap((r) =>
      r.member == null
        ? []
        : [{
            member_id: r.member.member_id,
            name: r.member.name,
            picture_profile: r.member.picture_profile,
            recent_engagement: r.recent_engagement ?? 0,
            prior_engagement: r.prior_engagement ?? 0,
            stage: (r.stage ?? 'silent') as MemberStage,
            momentum: r.momentum ?? 0,
          }],
    );
  }
  const warming = $derived(people(data?.momentum.warming ?? []));
  const cooling = $derived(people(data?.momentum.cooling ?? []));

  interface LeaderRow {
    member_id: number;
    name: string;
    picture_profile: string | null;
    posts: number;
    comments: number;
    activity: number;
    upvotes_received: number;
  }
  function leaders(rows: ContributorRow[]): LeaderRow[] {
    return rows.flatMap((r) =>
      r.member == null
        ? []
        : [{
            member_id: r.member.member_id,
            name: r.member.name,
            picture_profile: r.member.picture_profile,
            posts: r.posts,
            comments: r.comments,
            activity: r.activity,
            upvotes_received: r.upvotes_received,
          }],
    );
  }
  const topContributors = $derived(leaders(data?.top_contributors ?? []));

  void init();
</script>

<AdminShell {me}>

  {#if loading}
    <StateBlock state="loading" />
  {:else if error && !data}
    <StateBlock state="error" message={error} onretry={init} />
  {:else}
    <PageContainer>
      <!-- The community is named by the shell's header on every page, so this is the
           page's own title, like every sibling under /admin. The eyebrow said
           "Overview" above a title that said "Overview"; the section it actually
           lives in is Mission Control. -->
      <PageHeader section="Mission Control" title="Overview" updated={data?.last_updated} />

      {#if dataLoading}
        <StateBlock state="loading" />
      {:else if data}
        <!-- privacy-ok: binds a STATS block (counts), not written content -->
        {@const s = data.summary}
        {@const e = data.engagement}

        <!-- Money first: it is the one row on this page an owner checks daily, and the
             only one that says anything when the community is quiet. Stamped with the
             roster sweep rather than the page's window — see the component. -->
        <RevenueTiles revenue={data.revenue} updated={data.last_updated} />

        <!-- Row 1: best time to post · weekly activity (moved to top) -->
        <div class="grid gap-6 lg:grid-cols-2">
          <section class="rounded-box border hair bg-base-100 p-5">
            <div class="eyebrow mb-3">Most Active Time</div>
            <Clock cells={data.clock} />
          </section>
          <section class="rounded-box border hair bg-base-100 p-5">
            <TrendChart points={data.trend} height={150} />
          </section>
        </div>

        <!-- Row 2: daily activity heatmap -->
        <section class="mt-6 rounded-box border hair bg-base-100 p-5">
          <div class="mb-3 flex items-end justify-between">
            <div class="eyebrow">Daily activity · last {Math.round(WINDOW_DAYS / 30)} months</div>
            <div class="flex items-center gap-1.5 font-mono text-[0.7rem] text-base-content/75">
              <span>less</span>{#each HEAT as h}<span class="h-[11px] w-[11px] rounded-[2px] {h}"></span>{/each}<span>more</span>
            </div>
          </div>
          <Heatmap
            days={data.heatmap.days}
            max={data.heatmap.max}
            detailed
            onpick={(date) =>
              /* The heatmap's `date` is a UTC day — the server buckets on
                 to_char(external_created_at,'YYYY-MM-DD') over a UTC column. Send the
                 exact instants that cell counted, NOT the day string: the timeline
                 would otherwise read it as a LOCAL day and show a window shifted by
                 the viewer's offset, so the list would disagree with the cell. */
              navigate(
                `/admin/community/timeline?range=custom&since=${encodeURIComponent(utcDayStartISO(date))}` +
                  `&until=${encodeURIComponent(utcDayEndISO(date))}`,
              )}
          />
        </section>

        <!-- Contributors & Engagement — the role census + momentum strips, sharing the
             card the Contributors page mounts. It carries its OWN period control and
             its own request, so turning that dial does not re-window the heatmap above. -->
        {#if card}
          <!-- Kept MOUNTED while a period change is in flight, and dimmed instead of
               swapped for a spinner: the dial that triggered the reload lives inside
               this card, so unmounting it would make the control the reader just
               clicked disappear until the request came back. -->
          <ContributorsEngagement
            bind:days={cardDays}
            census={card.census}
            spark={card.census_spark}
            roleVolume={card.role_volume}
            class="mt-6 {cardLoading ? 'pointer-events-none opacity-60 transition-opacity' : 'transition-opacity'}"
          />
        {:else if cardLoading}
          <section class="mt-6 rounded-box border hair bg-base-100 p-5">
            <StateBlock state="loading" compact />
          </section>
        {:else if cardError}
          <section class="mt-6 rounded-box border hair bg-base-100 p-5">
            <StateBlock state="error" message={cardError} onretry={retryCard} compact />
          </section>
        {/if}

        <!-- Row 3: health headline — who's active + how fast posts get a first reply -->
        <div class="mt-6 grid gap-6 lg:grid-cols-2">
          <section class="rounded-box border hair bg-base-100 p-5">
            <div class="eyebrow mb-4">Active members · last 30 days</div>
            <div class="flex items-center gap-6">
              <Donut value={s.active_rate ?? 0} center={pct(s.active_rate)} sub="active" />
              <div class="flex-1 space-y-3 text-sm">
                <div><div class="font-medium">{num(s.active_members)} active</div><div class="text-base-content/72">of {num(s.total_members)} members posted or commented in the last 30 days</div></div>
                <div><div class="font-medium">{num(s.dormant)} dormant</div><div class="text-base-content/72">{s.dormant > 0 ? "haven’t posted in the last 30 days" : 'everyone active'}</div></div>
              </div>
            </div>
          </section>

          <section class="rounded-box border hair bg-base-100 p-5">
            <div class="eyebrow mb-4">How fast posts get a first reply · last 30 days</div>
            <div class="flex items-center gap-6">
              <Donut value={e.answered_within_24h_rate ?? 0} center={hrs(e.median_first_response_hours)} sub="median" color="var(--color-success)" />
              <div class="flex-1 space-y-3 text-sm">
                <div><div class="font-medium tabular-nums">{pct(e.answered_within_24h_rate)}</div><div class="text-base-content/72">of posts get a reply within 24h</div></div>
                <div><div class="font-medium tabular-nums {(e.unanswered_rate ?? 0) > 0.3 ? 'text-warning' : ''}">{num(e.unanswered_posts)}</div><div class="text-base-content/72">posts still waiting for a first reply</div></div>
              </div>
            </div>
          </section>
        </div>

        <!-- Row 4: content summary — what lands, by category -->
        <div class="mt-6">
          <ContentSummary labels={data.labels} timing={data.timing} windowLabel="last 12 months" />
        </div>

        <!-- Engagement momentum: who's warming up vs cooling off (second-last row) -->
        <div class="mt-6 grid gap-6 lg:grid-cols-2">
          {#snippet momentumList(title: string, hint: string, rows: PersonRow[], metric: 'recent' | 'prior')}
            <section class="rounded-box border hair bg-base-100 p-5">
              <div class="eyebrow mb-1">{title}</div>
              <p class="mb-3 text-xs text-base-content/75">{hint}</p>
              {#if rows.length === 0}
                <p class="text-sm text-base-content/62">Nobody right now.</p>
              {:else}
                <ul class="space-y-0.5">
                  {#each rows as r (r.member_id)}
                    <li>
                      <button class="flex w-full items-center gap-3 rounded-field px-2 py-1.5 text-left transition-colors hover:bg-base-200/60" onclick={() => navigate(`/admin/community/members/${r.member_id}`)}>
                        <MemberHover memberId={r.member_id} class="min-w-0 flex-1 items-center gap-3">
                          <Avatar src={r.picture_profile} name={r.name} size={28} memberId={r.member_id} />
                          <span class="min-w-0 flex-1">
                            <span class="redact block truncate text-sm font-medium">{r.name}</span>
                            <span class="redact block truncate font-mono text-[0.7rem] text-base-content/75">
                              {metric === 'recent' ? `${r.recent_engagement} now vs ${r.prior_engagement} before` : `was ${r.prior_engagement}, now ${r.recent_engagement}`}
                            </span>
                          </span>
                        </MemberHover>
                        <StageBadge stage={r.stage} momentum={r.momentum} />
                      </button>
                    </li>
                  {/each}
                </ul>
              {/if}
            </section>
          {/snippet}
          {@render momentumList('Warming up', 'New & re-energising members gaining momentum (last 14 days vs prior 14).', warming, 'recent')}
          {@render momentumList('Cooling off', 'Previously-active members losing steam — worth a nudge before they drift.', cooling, 'prior')}
        </div>

        <!-- Contributors + posts (last row) -->
        <div class="mt-6 grid gap-6 lg:grid-cols-2">
          <section class="rounded-box border hair bg-base-100 p-5">
            <div class="eyebrow mb-3">Top contributors · last 30 days</div>
            <ul class="space-y-0.5">
              {#each topContributors as m, i (m.member_id)}
                <li>
                  <button class="flex w-full items-center gap-3 rounded-field px-2 py-1.5 text-left transition-colors hover:bg-base-200/60" onclick={() => openMember(m.member_id)}>
                    <span class="w-4 text-right font-mono text-xs text-base-content/58">{i + 1}</span>
                    <MemberHover memberId={m.member_id} class="min-w-0 flex-1 items-center gap-3">
                      <Avatar src={m.picture_profile} name={m.name} size={28} memberId={m.member_id} />
                      <span class="min-w-0 flex-1">
                        <span class="redact block truncate text-sm font-medium">{m.name}</span>
                        <span class="redact block truncate font-mono text-[0.7rem] text-base-content/75">{m.posts}p · {m.comments}c · ▲{m.upvotes_received}</span>
                      </span>
                    </MemberHover>
                    <span class="font-mono text-xs tabular-nums text-base-content/75">{m.activity.toLocaleString()}</span>
                  </button>
                </li>
              {/each}
            </ul>
          </section>

          <section class="rounded-box border hair bg-base-100 p-5">
            <!-- v1 has ONE definition of "top posts" — windowed and staff-excluded
                 (doc 04 §5.2). The unwindowed, staff-inclusive list of the same name
                 that `/admin/analytics/community` also shipped does not survive, so
                 this is the page window's most-discussed posts, not the last 30 days'. -->
            <div class="eyebrow mb-3">Most discussed posts · last 12 months</div>
            <ul class="space-y-2.5">
              {#each data.top_posts.by_comments as p (p.id)}
                <li class="flex items-start gap-2.5">
                  <span class="mt-px shrink-0 rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[0.7rem] font-medium tabular-nums text-primary">💬{p.comment_count ?? 0}</span>
                  <div class="min-w-0">
                    <div class="redact truncate text-sm">{p.title || '(untitled)'}</div>
                    <div class="redact truncate font-mono text-[0.7rem] text-base-content/75">{p.author?.name ?? 'Unknown'} · ▲{p.upvotes ?? 0}</div>
                  </div>
                </li>
              {/each}
            </ul>
          </section>
        </div>
      {/if}
    </PageContainer>
  {/if}

  <!-- Member drill-down drawer -->
  <Drawer open={memberId != null} onclose={closeMember} label="Member details">
    {#if memberLoading}
      <StateBlock state="loading" />
    {:else if member}
      {@const m = member}
      {@const st = m.stats ?? null}
      {@const journey = journeySegments(m.journey)}
      <div class="flex items-start gap-4 border-b hair p-6">
        <Avatar src={m.picture_profile} name={m.name} size={56} memberId={m.member_id} />
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-center gap-2">
            <h2 class="redact font-display text-xl font-bold tracking-tight">{m.name}</h2>
            <StageBadge stage={(st?.stage ?? 'silent') as MemberStage} momentum={st?.momentum ?? null} />
          </div>
          <!-- `handle` is genuinely nullable in v1 (§2.1); the legacy shape typed it as
               a bare string only because the server substituted "unknown". -->
          <div class="redact font-mono text-xs text-base-content/68">@{m.handle ?? 'unknown'}</div>
          <div class="mt-1.5 flex flex-wrap gap-x-3 font-mono text-[0.7rem] text-base-content/75">
            {#if m.location}<span>{m.location}</span>{/if}
            {#if m.membership?.mrr_status}<span class="text-primary/70">{m.membership.mrr_status}</span>{/if}
          </div>
        </div>
        <div class="flex shrink-0 items-center gap-1">
          <button class="btn btn-ghost btn-sm gap-1 text-primary/80" onclick={() => navigate(`/admin/community/members/${m.member_id}`)}>
            Full profile →
          </button>
          <button class="btn btn-ghost btn-sm" onclick={closeMember} aria-label="Close">✕</button>
        </div>
      </div>

      <!-- `stats` is the precomputed all-time block, so these three are all-time —
           where the legacy drawer showed a 30-day window recomputed from the member's
           whole content history. `replies_earned` is the one direction v1 stores;
           "replies out" was derived by the reply-network scan and goes with it. -->
      <div class="grid grid-cols-3 gap-3 p-6">
        <StatCard label="Activity" value={num(st?.activity)} sub={`${st?.posts ?? 0}p · ${st?.comments ?? 0}c`} tone="primary" />
        <StatCard label="Upvotes" value={num(st?.upvotes_received)} sub="received" />
        <StatCard label="Replies earned" value={num(st?.replies_earned)} sub="on their content" />
      </div>

      <div class="px-6 pb-1 font-mono text-[0.7rem] text-base-content/75">Active {fmtDate(st?.first_active)} → {fmtDate(st?.last_active)} · all time</div>

      {#if journey.length}
        <section class="px-6 py-3">
          <div class="eyebrow mb-2">Role journey</div>
          <RoleJourney {journey} />
        </section>
      {/if}

      <section class="px-6 py-3 pb-6">
        <div class="eyebrow mb-2">Activity · last 12 months</div>
        <!-- `activity=dense`: <Heatmap> pads from the FIRST day's weekday and walks
             forward, so it needs the contiguous 371-day series, never `weekly`. -->
        <Heatmap days={m.activity?.days ?? []} max={m.activity?.max ?? 0} months={false} showLabels={false} gap={2} />
      </section>
    {/if}
  </Drawer>
</AdminShell>
